import React from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"

import Layout from "../../components/Layout/Layout"
import RecipeDetailPage from "../../components/UI/RecipeDetailPage/RecipeDetailPage"
import RelatedRecipes from "../../components/UI/RelatedRecipes/RelatedRecipes"
import GlobalContext from "../../context/GlobalContext"
import Link from "../../components/UI/Link/Link"
import { setDataLayer } from "../../helpers/dataLayerSetter"

import componentMaker from "../../helpers/ComponentMaker/componentMaker"
import { API_SIMILAR_RECIPE_SHAPE, TASTEHUB_RECIPE_SHAPE } from "../../constants/shapes.constants"
import { makePerex } from "../../helpers/DataConvertor.helper"
import { exportMeta } from "../../helpers/utils"

import SchemaSnippet from "../../components/SchemaSnippet/SchemaSnippet"
import { gTagScriptConsent } from "../../components/Scripts/Gtm.Head"
import { DEFAULT_LOCALE } from '../../constants/mondelezAPI.contants'

const recipeTime = time => {
    return `PT${time}M`
}

const RecipeDetail = ({ path, pageContext }) => {
    const { apiData, similarRecipes, globalComponents, seoData, pageType, dataLayer, globalLabels, brandTheme, languageCode, legalOctagons } = pageContext
    const { staticResult, contentfulRecipeDetail } = pageContext.pageProps

    const finalSeoData = seoData || {}
    finalSeoData.title = apiData?.title
    finalSeoData.metaDescription = makePerex(apiData?.romance_text, 200)
    finalSeoData.featuredImage = apiData?.images?.[0]?.image_url ? { url: apiData.images[0].image_url } : null
    finalSeoData.ogUrl = path
    finalSeoData.metaKeywords = exportMeta(apiData?.metatags).keywords
    finalSeoData.sku = apiData?.id
    finalSeoData.category = apiData?.brands?.[0]

    setDataLayer(dataLayer, finalSeoData, pageType)

    return (
        <GlobalContext countryCode={languageCode || DEFAULT_LOCALE} labels={globalLabels} brandTheme={brandTheme}>
            <Layout path={path} seoData={finalSeoData} globalComponents={globalComponents} languageCode={languageCode} legalOctagons={legalOctagons}>
                <RecipeDetailPage apiData={apiData} shareUrl={path} />

                <SchemaSnippet
                    snippet={`
                {
                    "@context": "https://schema.org/",
                    "@type": "Recipe",
                    "name": "${apiData?.title.replace(/(<([^>]+)>)/gi, "")}",
                    "image": "${apiData?.images?.[0]?.image_url}",
                    "description": "${apiData?.romance_text?.replaceAll('"', "'").replace(/(<([^>]+)>)/gi, "")}",
                    "keywords": "${exportMeta(apiData?.metatags).keywords}",
                    "author": {
                        "@type": "Person",
                        "name": "Snackworks"
                    },
                    "datePublished": "${dayjs().format("YYYY-MM-DD")}",
                    "prepTime": "${recipeTime(apiData?.times?.[0]?.minutes)}",
                    "totalTime": "${recipeTime(apiData?.times?.[1]?.minutes)}",
                    "recipeCategory": "${apiData?.RecipeCategorySubCategories?.[0]?.CategoryName.replace(
                        /(<([^>]+)>)/gi,
                        ""
                    )}",
                    "recipeYield": "${apiData?.portions}",
                    "recipeIngredient": [
                        ${apiData?.ingredients.map(
                        e =>
                                `"${e.amount} ${e.ingredient_unit?.name?.replace(
                                    /(<([^>]+)>)/gi,
                                    ""
                                )} ${e.ingredient?.name?.replace(/(<([^>]+)>)/gi, "")}${e.postwords?.replace(
                                    /(<([^>]+)>)/gi,
                                    ""
                                )}"`
                    ).join(",\n")}
                    ]
                }
                `}
                />

                <div className="print:hidden">{componentMaker(staticResult.contentComponents)}</div>

                <RelatedRecipes similarRecipes={similarRecipes} link={contentfulRecipeDetail.relatedRecipesLink} />

                <div className="print:hidden">{componentMaker(staticResult.bottomContentComponents)}</div>
            </Layout>
        </GlobalContext>
    )
}

RecipeDetail.propTypes = {
    path: PropTypes.string,
    pageContext: PropTypes.shape({
        pageProps: PropTypes.shape({
            staticResult: PropTypes.object,
            contentfulRecipeDetail: PropTypes.shape({
                relatedRecipesLink: PropTypes.shape(Link.propTypes),
            }),
        }),
        apiData: PropTypes.shape(TASTEHUB_RECIPE_SHAPE),
        similarRecipes: PropTypes.arrayOf(PropTypes.shape(API_SIMILAR_RECIPE_SHAPE)),
        globalComponents: PropTypes.array,
        seoData: PropTypes.object,
        pageType: PropTypes.string,
        dataLayer: PropTypes.object,
        globalLabels: PropTypes.object,
        brandTheme: PropTypes.shape({
            themeName: PropTypes.string,
        }),
        languageCode: PropTypes.string,
        legalOctagons: PropTypes.object,
    }),
}

/**
 * Adding to Layout ?
 */
export const Head = () => {
    const dataObject = typeof window !== "undefined" ? window?.gtmDataLayer : {}

    return (
        <>
            <script
                id="first-unique-id"
                type="text/javascript"
                dangerouslySetInnerHTML={{
                    __html: `var MdlzdataLayer = ${JSON.stringify(dataObject)};`,
                }}
            />
            <script
                async
                type="text/javascript"
                dangerouslySetInnerHTML={{ __html: gTagScriptConsent }}
            />
            <script src="https://shop.basketful.co/basketful.js" async />

        </>
    )
}

export default RecipeDetail
